import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DashboardLayout from '../../components/layout/DashboardLayout';
import UserSidebar from '../../components/sidebar/UserSidebar';
import DocumentUploadInput from '../../components/DocumentUploadInput';
import './DocumentUploadPage.css';
import { AppDispatch, RootState } from '../../redux/store';
import {
  fetchUserDocuments,
  clearDocumentUploadError,
  resetUploadStatus,
  uploadAllDocuments,
  clearBatchUploadResults,
  reuploadDocument
} from '../../redux/documentUploadSlice';

const DocumentUploadPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    documents,
    loading,
    error,
    uploadProgress,
    uploadSuccess
  } = useSelector((state: RootState) => state.documentUpload as import("../../redux/documentUploadSlice").DocumentUploadState);

  const [selectedFiles, setSelectedFiles] = useState<{[key: string]: File | null}>({
    nin: null,
    cac: null,
    tin: null,
    passport: null,
    utility: null
  });

  const [uploadErrors, setUploadErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    dispatch(fetchUserDocuments());
  }, [dispatch]);

  useEffect(() => {
    if (uploadSuccess) {
      alert('Documents uploaded successfully!');
      dispatch(resetUploadStatus());
      dispatch(fetchUserDocuments());
      setSelectedFiles({
        nin: null,
        cac: null,
        tin: null,
        passport: null,
        utility: null
      });
      setUploadErrors({});
    }
  }, [uploadSuccess, dispatch]);

  useEffect(() => {
    if (error) {
      alert(`Error: ${error}`);
      dispatch(clearDocumentUploadError());
    }
  }, [error, dispatch]);

  const handleFileSelect = (documentType: string, file: File | null) => {
    setSelectedFiles(prev => ({
      ...prev,
      [documentType]: file
    }));
    setUploadErrors(prev => ({
      ...prev,
      [documentType]: ''
    }));
  };

  const getDocumentStatus = (documentType: string) => {
    const doc = documents.find(d => d.name === documentType);
    return doc?.status;
  };

  const handleReupload = async (documentType: string) => {
    const file = selectedFiles[documentType];
    if (!file) {
      alert(`Please select a new ${documentType.toUpperCase()} document to reupload.`);
      return;
    }

    const existingDoc = documents.find(d => d.name === documentType);
    if (!existingDoc) {
      alert(`No existing ${documentType.toUpperCase()} document found to reupload.`);
      return;
    }

    try {
      await dispatch(reuploadDocument({ file, documentType, existingDocumentId: existingDoc.id })).unwrap();
      alert(`${documentType.toUpperCase()} document reuploaded successfully!`);
      dispatch(fetchUserDocuments());
      setSelectedFiles(prev => ({
        ...prev,
        [documentType]: null
      }));
    } catch (error) {
      alert(`Failed to reupload ${documentType.toUpperCase()} document. Please try again.`);
      console.error('Reupload error:', error);
    }
  };

  const handleUploadAll = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if all required documents are selected
    const requiredTypes = ['nin', 'cac', 'tin', 'passport', 'utility'];
    const missingDocuments: string[] = [];

    requiredTypes.forEach(type => {
      if (!selectedFiles[type]) {
        missingDocuments.push(type.toUpperCase());
      }
    });

    if (missingDocuments.length > 0) {
      alert(`Please select all required documents. Missing: ${missingDocuments.join(', ')}`);
      return;
    }

    // Prepare documents for batch upload
    const documentsToUpload = requiredTypes.map(documentType => ({
      file: selectedFiles[documentType]!,
      documentType
    }));

    // Clear previous batch results
    dispatch(clearBatchUploadResults());

    // Upload all documents in batch
    try {
      const results = await dispatch(uploadAllDocuments({ documents: documentsToUpload })).unwrap();

      // Check results and provide feedback
      const successfulUploads = results.filter(result => result.success);
      const failedUploads = results.filter(result => !result.success);

      if (failedUploads.length === 0) {
        alert('All documents uploaded successfully! They are now pending admin approval.');
        dispatch(fetchUserDocuments());
        setSelectedFiles({
          nin: null,
          cac: null,
          tin: null,
          passport: null,
          utility: null
        });
      } else {
        const failedTypes = failedUploads.map(result => result.documentType.toUpperCase()).join(', ');
        alert(`Some documents failed to upload: ${failedTypes}. Successful uploads: ${successfulUploads.length}. Please try again for failed documents.`);
        // Refresh documents to show successful ones
        dispatch(fetchUserDocuments());
      }
    } catch (error) {
      alert('Batch upload failed. Please try again.');
      console.error('Batch upload error:', error);
    }
  };

  return (
    <DashboardLayout sidebar={<UserSidebar />}>
      <div className="document-upload-page">
        <div className="page-header">
          <h2>Document Upload & Verification</h2>
          <p>Upload required documents for account verification</p>
        </div>

        <div className="document-upload-form">
          <div className="document-types-grid">
            <div className="document-type-section">
              <h3>National Identity Number (NIN)</h3>
              <DocumentUploadInput
                label="Upload NIN Document"
                onFileSelect={(file) => handleFileSelect('nin', file)}
                acceptedTypes=".pdf,.jpg,.jpeg,.png"
                maxSize={5}
                error={uploadErrors.nin}
                documentStatus={getDocumentStatus('nin')}
                onReupload={() => handleReupload('nin')}
              />
            </div>

            <div className="document-type-section">
              <h3>Corporate Affairs Commission (CAC)</h3>
              <DocumentUploadInput
                label="Upload CAC Document"
                onFileSelect={(file) => handleFileSelect('cac', file)}
                acceptedTypes=".pdf,.jpg,.jpeg,.png"
                maxSize={5}
                error={uploadErrors.cac}
                documentStatus={getDocumentStatus('cac')}
                onReupload={() => handleReupload('cac')}
              />
            </div>

            <div className="document-type-section">
              <h3>Tax Identification Number (TIN)</h3>
              <DocumentUploadInput
                label="Upload TIN Document"
                onFileSelect={(file) => handleFileSelect('tin', file)}
                acceptedTypes=".pdf,.jpg,.jpeg,.png"
                maxSize={5}
                error={uploadErrors.tin}
                documentStatus={getDocumentStatus('tin')}
                onReupload={() => handleReupload('tin')}
              />
            </div>

            <div className="document-type-section">
              <h3>Passport Photograph</h3>
              <DocumentUploadInput
                label="Upload Passport Photo"
                onFileSelect={(file) => handleFileSelect('passport', file)}
                acceptedTypes=".jpg,.jpeg,.png"
                maxSize={2}
                error={uploadErrors.passport}
                documentStatus={getDocumentStatus('passport')}
                onReupload={() => handleReupload('passport')}
              />
            </div>

            <div className="document-type-section">
              <h3>Utility Bill</h3>
              <DocumentUploadInput
                label="Upload Utility Bill"
                onFileSelect={(file) => handleFileSelect('utility', file)}
                acceptedTypes=".pdf,.jpg,.jpeg,.png"
                maxSize={5}
                error={uploadErrors.utility}
                documentStatus={getDocumentStatus('utility')}
                onReupload={() => handleReupload('utility')}
              />
            </div>
          </div>

          <div className="submit-all-section">
            <button
              type="button"
              onClick={handleUploadAll}
              disabled={loading}
              className="submit-all-btn"
            >
              {loading ? 'Uploading Documents...' : 'Submit All Documents for Approval'}
            </button>
          </div>
        </div>

        {uploadProgress > 0 && (
          <div className="upload-progress">Upload Progress: {uploadProgress}%</div>
        )}

        <div className="uploaded-documents">
          <h3>Your Uploaded Documents</h3>
          {loading && documents.length === 0 ? (
            <p>Loading documents...</p>
          ) : documents.length === 0 ? (
            <p>No documents uploaded yet.</p>
          ) : (
            <ul>
              {documents.map((doc: import("../../redux/documentUploadSlice").Document) => (
                <li key={doc.id}>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer">{doc.name}</a> - <span>{doc.status}</span>
                  {doc.status === 'rejected' && doc.rejectionReason && (
                    <div className="doc-feedback">Feedback: {doc.rejectionReason}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DocumentUploadPage;
